import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import {
  Button, Card, Col, Divider, Flex, Row,
  Typography,
} from 'antd';
import NodeMenu from 'automations/components/NodeMenu';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useAutomationId from 'automations/hooks/useAutomationId';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import {
  type LeadActivityUpdateNodeType,
  nodeSchemas,
} from 'automations/types/nodeTypes';
import { getParsedTriggerActivityConfig } from 'automations/utils/triggeredActivityConfig';
import { saveTriggerNode } from 'automations/utils/triggerNode';
import {
  useCallback, useMemo, useState,
} from 'react';
import { ZodError } from 'zod';

import AutomationsCriteriaConditionsWrapper from '../AutomationsCriteriaConditionsWrapper';
import { type BuilderUiProps } from '../AutomationsCriteriaConditionsWrapper/AutomationsCriteriaConditionsWrapper';
import ShowConditionsUi from '../ShowConditionsUi';
import ActivityUpdateSelection from './ActivityUpdateSelection';
import styles from './LeadActivityUpdateMenu.module.scss';

export type ActivityUpdateCriteria = {
  activityId: number | null;
  activityAttributeId: number | null;
  fromValue?: string;
  toValue?: string | string[];
};

export default function LeadActivityUpdateMenu({
  node,
}: {
  node: NodeProps<LeadActivityUpdateNodeType>;
}) {
  const dispatch = useAutomationDispatch();
  const automationId = useAutomationId();
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const [isLoading, setIsLoading] = useState(false);

  const [activityUpdateConfig, setActivityUpdateConfig] = useState<ActivityUpdateCriteria>(() => {
    const existingConfig = node.data.additionalOptions?.updatedActivity;

    return {
      activityId: existingConfig?.activityId ?? null,
      activityAttributeId: existingConfig?.activityAttributeId ?? null,
      fromValue: existingConfig?.fromValue ?? 'any',
      toValue: existingConfig?.toValue ?? 'any',
    };
  });

  const [additionalConditionsId, setAdditionalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.criteriaGroupId,
  );
  const [removalConditionsId, setRemovalConditionsId] = useState<number | undefined>(
    node.data.additionalOptions?.removableCriteriaGroupId,
  );

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      if (!activityUpdateConfig.activityId) {
        message.error('Please select an activity');
        return;
      }

      if (!activityUpdateConfig.activityAttributeId) {
        message.error('Please select an activity field');
        return;
      }

      const parsedNodeData = nodeSchemas.LeadActivityUpdateNodeData.parse({
        ...node.data,
        additionalOptions: {
          updatedActivity: {
            activityId: activityUpdateConfig.activityId,
            activityAttributeId: activityUpdateConfig.activityAttributeId,
            fromValue: activityUpdateConfig.fromValue,
            toValue: activityUpdateConfig.toValue,
          },
          criteriaGroupId: additionalConditionsId,
          removableCriteriaGroupId: removalConditionsId,
        },
      });

      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: parsedNodeData.additionalOptions,
      }));

      await dispatch(saveTriggerNode(node.id, automationId, contentMode));

      message.success(translate(`#{${TranslationEntity.automation}} updated`, [{ capitalize: true }]));
      setMenuOpen(false);
    } catch (error: any) {
      const errorMessage = error instanceof ZodError
        ? error.issues.map((issue) => issue.message).join(', ')
        : error?.data?.message ?? translate(
          `Failed to update #{${TranslationEntity.automation}}`,
          [{ capitalize: true }],
        );
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [activityUpdateConfig, additionalConditionsId, automationId, contentMode, dispatch, node.data, node.id,
    removalConditionsId, setMenuOpen,
  ]);

  const triggeredActivityConfig = useMemo(() => {
    if (activityUpdateConfig.activityId) {
      return [getParsedTriggerActivityConfig([{ activityId: activityUpdateConfig.activityId }])];
    }
    return [];
  }, [activityUpdateConfig.activityId]);

  const ConditionsBuilderUi = useCallback((props: BuilderUiProps) => (
    <ShowConditionsUi
      {...props}
      title="Set Conditions"
      btnClassName={styles.addBtn}
    />
  ), []);

  return (
    <NodeMenu
      node={node}
      drawerProps={{
        title: translate(
          `#{${TranslationEntity.automation}} Trigger - When #{${TranslationEntity.activity}} is updated`,
          [{ capitalize: true }, { capitalize: true, withArticle: true }],
        ),
      }}
    >
      <Card title={contentMode === 'create' ? 'Set Trigger' : 'Edit Trigger'} className={styles.containerCard}>
        <ActivityUpdateSelection
          activityUpdateConfig={activityUpdateConfig}
          onConfigChange={setActivityUpdateConfig}
        />

        <Divider dashed />
        <Row align="middle">
          <Col span={6}>
            <Typography.Text strong>Set Additional Conditions</Typography.Text>
          </Col>
          <Col span={18}>
            <AutomationsCriteriaConditionsWrapper
              criteriaId={additionalConditionsId}
              onCriteriaChange={setAdditionalConditionsId}
              additionalConditionKeys={triggeredActivityConfig}
              builderUi={ConditionsBuilderUi}
            />
          </Col>
        </Row>
        <Divider dashed />
        <Row align="middle">
          <Col span={6}>
            <Typography.Text strong>
              {translate(
                `Choose condition(s) to remove #{${TranslationEntity.lead}}(s)
                 from this #{${TranslationEntity.automation}}`,
                [{ capitalize: true }],
              )}
            </Typography.Text>
          </Col>
          <Col span={18}>
            <AutomationsCriteriaConditionsWrapper
              criteriaId={removalConditionsId}
              onCriteriaChange={setRemovalConditionsId}
              builderUi={ConditionsBuilderUi}
              additionalConditionKeys={triggeredActivityConfig}
            />
          </Col>
        </Row>
      </Card>

      <Flex justify="flex-end" gap={16}>
        <Button onClick={() => setMenuOpen(false)}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={isLoading}
          disabled={isLoading}
        >
          Set Trigger
        </Button>
      </Flex>
    </NodeMenu>
  );
}
